import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import { Observable, BehaviorSubject, tap, throwError, catchError } from 'rxjs';
import { MatchupService } from './matchup.service';
import { MultiWheelOutcome, MultiWheelSpin } from '@shared/types/spin';
import { SessionService } from './session.service';
import { Page } from '@longnecktech/splash-commons-fe';

@Injectable({
  providedIn: 'root',
})
export class SpinService {
  private http = inject(HttpClient);
  private matchupService = inject(MatchupService);
  private session = inject(SessionService);

  private _spinning = new BehaviorSubject<boolean>(false);
  private _spin = new BehaviorSubject<MultiWheelSpin | undefined>(undefined);
  private _hasUnseenResults = new BehaviorSubject<boolean>(false);
  private _unseenWonSpin = new BehaviorSubject<MultiWheelSpin | undefined>(
    undefined,
  );

  readonly spinning$ = this._spinning.asObservable();
  readonly spin$ = this._spin.asObservable();
  readonly hasUnseenResults$ = this._hasUnseenResults.asObservable();
  readonly unseenWonSpin$ = this._unseenWonSpin.asObservable();

  updateSpinning(data: boolean): void {
    this._spinning.next(data);
  }

  getSpinningValue(): boolean {
    return this._spinning.getValue();
  }

  onSpin(gameUuid: string): Observable<MultiWheelSpin> {
    const questionUuid = this.matchupService.matchup?.uuid;

    return this.http
      .post<MultiWheelSpin>(
        `${environment.backendUrl}/api/multi-wheel/spin/${gameUuid}?questionUuid=${questionUuid}`,
        {},
      )
      .pipe(
        tap((spin: MultiWheelSpin) => {
          this.setSpin(spin);

          const currentGame = this.session.getCurrentGame();
          if (currentGame) {
            this.session.setCurrentGame({ ...currentGame, hasPlayed: true });
          }
        }),
        catchError((error: Error) => {
          return throwError(() => new Error(error.message));
        }),
      );
  }

  setSpin(data: MultiWheelSpin): void {
    this._spin.next(data);
  }

  getHistory(page = 0, size = 5): Observable<Page<MultiWheelSpin>> {
    const instance = this.session.instance;
    let url = `${environment.backendUrl}/api/multi-wheel/spin/history?page=${page}&size=${size}`;
    if (instance) {
      url += `&instance=${instance}`;
    }
    return this.http.get<Page<MultiWheelSpin>>(url).pipe(
      tap((page) => {
        const spins = page.content || [];
        const hasUnseen = (page.content || []).some(
          (spin) =>
            !spin.shown &&
            spin.outcome &&
            spin.outcome !== MultiWheelOutcome.PENDING,
        );

        const unseenWonSpin = spins.find(
          (spin) => !spin.shown && spin.outcome === MultiWheelOutcome.WON,
        );

        this._hasUnseenResults.next(hasUnseen);
        this._unseenWonSpin.next(unseenWonSpin);
      }),
    );
  }

  clearUnseenWonSpin(): void {
    this._unseenWonSpin.next(undefined);
  }

  markAsSeen(uuids: string[]): Observable<void> {
    return this.http
      .post<void>(
        `${environment.backendUrl}/api/multi-wheel/spin/history/mark-seen`,
        uuids,
      )
      .pipe(
        tap(() => {
          this._hasUnseenResults.next(false);
        }),
      );
  }
}
